import {AChartX} from "@atiautomacao/ati-ui-library";
import React, {FunctionComponent} from "react";
import {useAppSelector} from "../../../../Config/Hooks";
import {useTheme} from "@mui/material";
import UnavailabilityEmptyState, {UnavailabilityErrorState} from "./UnavailabilityEmptyState";
import {UnavailabilityPowerStation} from "../../../../Config/Types";
import {
    aggregateUnavailabilityByStation,
    buildDinTooltipHtml,
    CHART_SERIES,
    hasUnavailabilityLoss,
    stackedChartSeries,
    limitStations,
    OTHERS_STATION_ID,
    UnavailabilityChartMode
} from "../utils/UnavailabilityChartUtils";

const UnavailabilityBarChart: FunctionComponent<{data?: UnavailabilityPowerStation[], mode?: UnavailabilityChartMode, isExpansive?: boolean, height?:number, isError?: boolean}> = ({data, mode = 'TOTAL', isExpansive = false, height = 400, isError = false}) => {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const theme = useTheme();
    const series = CHART_SERIES[mode];
    // Séries na ordem de empilhamento (base -> topo); a legenda mantém a ordem de CHART_SERIES.
    const stackedSeries = React.useMemo(() => stackedChartSeries(mode), [mode]);

    const processedData = React.useMemo(() => {
        const seriesKeys = series.map(item => item.key);
        return limitStations(aggregateUnavailabilityByStation(data, seriesKeys), seriesKeys, isExpansive);
    }, [data, series, isExpansive]);

    const grandTotal = React.useMemo(() => processedData.reduce((sum, station) => sum + station.total, 0), [processedData]);

    // @ts-ignore
    const option: AChartXProps['option'] = React.useMemo(() => ({
        color: stackedSeries.map(item => item.color),
        toolbox: {show: false },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            ...(isExpansive && mode === 'DIN' ? {
                backgroundColor: '#ffffff',
                borderWidth: 0,
                padding: [12, 16],
                extraCssText: 'border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.15);',
                formatter: (params: {dataIndex: number}[]) => {
                    const station = processedData[params[0]?.dataIndex];
                    return station ? buildDinTooltipHtml(station, grandTotal) : '';
                },
            } : {}),
        },
        legend: {
            data: series.map(item => item.label),
            bottom: 0,
        },
        grid: {
            containLabel: true,
            top: 10,
            bottom: 30,
            right: 50,
            left: 40,
        },
        xAxis: {
            type: 'category',
            data: processedData.map(item => item.name),
            axisLabel: {
                interval: 0,
                rotate: 40,
                formatter: function(value:any) {
                    return value.length > 10 ? value.substring(0, 10) + '...' : value;
                }}
        },
        yAxis: {
            type: 'value',
            name: 'kWh',
            nameRotate: 90,
            nameLocation: 'middle',
            nameGap: 40,
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: '#e0e0e0'
                }
            },
            axisLine: { show: false },
            axisTick: { show: false }
        },
        series: stackedSeries.map((item, index) => ({
            name: item.label,
            type: 'bar',
            stack: 'total',
            data: processedData.map(station => ({
                value: station.values[item.key],
                itemStyle: { color: station.id === OTHERS_STATION_ID ? item.othersColor : item.color }
            })),
            ...(index === stackedSeries.length - 1 ? { itemStyle: { borderRadius: [4, 4, 0, 0] } } : {})
        }))
    }),[processedData, grandTotal, series, stackedSeries, mode, isExpansive, theme.palette.mode]);

    if (!hasUnavailabilityLoss(processedData)) {
        // Se um refetch em segundo plano falhar, os dados já carregados continuam valendo;
        // o erro só é exibido quando não há nenhum dado para mostrar.
        if (isError && !data) return <UnavailabilityErrorState height={`${height}px`}/>;
        return <UnavailabilityEmptyState height={`${height}px`}/>;
    }

    return (
        <AChartX
            // O modo faz parte da key para o ECharts não mesclar as séries do modo anterior.
            key={`din-dex-chart-${mode}-${openSubMenu}`}
            option={option}
            width={'100%'}
            height={height}
            loading={false}
            theme={theme.palette.mode}
        />
    );
}

export default UnavailabilityBarChart;
